import { useMemo, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Server } from "@/Common/Operations/operations.server";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { AuthenticatorCodeValidator } from "@/Common/Validators/AuthenticatorCodeValidator";
import { LoginPageState } from "../LoginPage";

import styles from "./LoginAuthenticatorCodeView.module.scss";

interface ILoginAuthenticatorCodeViewProps {
    state: LoginPageState;
    stringProvider: ResourceProvider;
    errorMessageJsx: JSX.Element;
}

function LoginAuthenticatorCodeView(props: ILoginAuthenticatorCodeViewProps): JSX.Element {
    const state = props.state;
    const [sendByEmailLinkDisabled, setSendByEmailLinkDisabled] = useState<boolean>(false);
    const { getText } = useTranslation(props.stringProvider);
    const authenticatorCodeValidator = useMemo(() => new AuthenticatorCodeValidator(), []);

    function handleKeyPress(e: React.KeyboardEvent): void {
        if (!state.model.get("ValidatingCredentials") && state.model.get("AuthenticatorCode")?.trim() && e.key === "Enter")
            enterKeyPress();
    }

    async function sendCodeByEmail(): Promise<void> {
        const sendCodeOperation = cacheStore.ClinicSettings.EnableMultifactorAuthenticationModule
            ? Server.Myle_MultifactorAuthenticationModule_Login_SendCodeByEmail
            : Server.Myle_MultifactorAuthenticator_SendCodeByEmail;

        const response = await Client.postAsync(
            sendCodeOperation,
            {
                LoginToken: sessionStore.clientLoginModel.get("SendAuthenticatorCodeByEmailToken")
            }
        );

        if (!response.isSuccess)
            return;

        displayToast(MessageToast.Success, getText("CodeSentLabel"), {
            hideProgressBar: false,
            pauseOnFocusLoss: false,
            autoClose: 3000,
            pauseOnHover: false,
            closeOnClick: false
        });
    }

    useMount(() => {
        if (cacheStore.ClinicSettings?.EnableMultifactorAuthenticationByEmail && sessionStore.clientLoginModel.get("SelectedAuthenticatorCodeDeliveryMethod") === AuthenticatorCodeDeliveryMethod.Email) {
            sendCodeByEmail();
        }
    });

    function enterKeyPress(): void {
        sessionStore.dispatch(LocalOperations.Login.OnLoginSubmit);
    }

    function onAuthenticatorCodeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        sessionStore.dispatch(LocalOperations.Login.AuthenticatorCode_TextChange, { Value: event.target.value.replace(/\D/g, "") });
    }

    function onVerifyClick(): void {
        sessionStore.dispatch(LocalOperations.Login.OnLoginSubmit);
    }

    const isValidatingCredentials = state.model.get("ValidatingCredentials");
    const isLoggedIn = state.model.get("LoggedIn");
    const isAuthenticatorCodeValid = authenticatorCodeValidator.validate(state.model.get("AuthenticatorCode"));
    const errorMessage = state.model.get("ErrorMessage").Message;

    const showCodeInput = !isValidatingCredentials && !isLoggedIn;
    const showSendCodeByEmailLink = showCodeInput && !cacheStore.ClinicSettings.EnableAppOnlyMultifactorAuthentication;

    return (
        <>
            <div className="f-row">
                <p className={styles.title}>{getText("MultifactorAuthentication_PleaseEnterCodeLabel")}</p>
                <div className="f-row dm">
                    <label className="f16px" htmlFor="authenticatorCode">
                        {getText("AuthenticatorCodeLabel")}
                    </label>
                    {showCodeInput && (
                        <input
                            type="text"
                            id="authenticatorCode"
                            data-cy="login-authenticator-code"
                            onKeyUp={e => handleKeyPress(e)}
                            className={`form-control ${styles.authenticatorCode} ${IsNullOrWhiteSpace(errorMessage) ? "" : "invalid"}`}
                            maxLength={AuthenticatorCodeValidator.codeLength}
                            placeholder={getText("AuthenticatorCodePlaceholder")}
                            onChange={e => onAuthenticatorCodeChange(e)}
                            value={state.model.get("AuthenticatorCode")}
                            autoComplete="off"
                            autoFocus={true}
                        />
                    )}
                </div>
            </div>

            <div className="f-row">{props.errorMessageJsx}</div>

            <div className="f-row">
                <button
                    disabled={!isAuthenticatorCodeValid || isValidatingCredentials || isLoggedIn}
                    className="btn btn-primary submit"
                    id="loginButton"
                    data-cy="login-button"
                    onClick={() => onVerifyClick()}
                    type="button"
                >
                    {getText("MultifactorAuthentication_VerifyLabel")}
                </button>
                {showSendCodeByEmailLink && (
                    <div className={styles.sendByEmailContainer}>
                        <a
                            onClick={async () => {
                                if (sendByEmailLinkDisabled)
                                    return;

                                setSendByEmailLinkDisabled(true);

                                sendCodeByEmail();

                                setTimeout(() => setSendByEmailLinkDisabled(false), 3000);
                            }}
                        >
                            {getText("SendByEmailLabel")}
                        </a>
                    </div>
                )}
            </div>
        </>
    );
}

export { LoginAuthenticatorCodeView };
